import { useRouter } from 'next/router';
import { createContext, useCallback, useContext, useState, useEffect, useMemo } from 'react';
import { AuthService, OneLoginService } from 'services/AuthService';
import {
  AuthTypeEnum,
  AuthStatusEnum,
  validateAuth,
  getProfileData,
  deleteAuthCookie,
  onUserLoginCT,
} from './utils';
import ProfileService from 'services/ProfileService';
import { EditageConfig } from 'config/onelogin.config';
import Cookies from 'js-cookie';
import { parseJwt } from 'utils';
import JournalService from 'services/JournalService';

const AuthUserContext = createContext();
const AuthUserOnlyContext = createContext(null);
const AuthUserActionsContext = createContext(null);
const AuthUserWorkspaceContext = createContext(null);

const LOGOUT_PAGE_PATH = '/auth/logout';
const TOKEN_VALIDATION_INTERVAL = 10 * 60 * 1000; // 10 minutes, based on the API cache

export function AuthUserProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [lastTokenValidationTimestamp, setLastTokenValidationTimestamp] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [userJournalSettings, setUserJournalSettings] = useState(null);
  const [shouldCompleteProfile, setShouldCompleteProfile] = useState(false);
  const [authType, setAuthType] = useState(AuthTypeEnum.SSO);
  const router = useRouter();

  /**
   * Update the user data in the context, and handles the side-effects of it
   *
   * @param {Object} userData
   */
  const updateUser = useCallback(
    async (userData, authType = AuthTypeEnum.SSO) => {
      let data = userData;

      if (!userData) {
        const response = await new ProfileService().getUser(user?.id);
        data = response.data;
      }

      setUser(data);
      const profile = await getProfileData(data.id);
      setProfile(profile);
      const userJournalSettings = await JournalService.getUserSettings();
      setUserJournalSettings(userJournalSettings.data);
      const isProfileComplete = true;
      setShouldCompleteProfile(!isProfileComplete);
      setIsAuthenticated(true);
      setAuthType(authType);
      onUserLoginCT(profile, data);
    },
    [user?.id],
  );

  /**
   * Complete the user profile, and updates the context data
   *
   * @param {Object} profileData
   * @returns {void}
   */
  const completeProfile = useCallback(async (profileData) => {
    const response = await AuthService.completeProfile(profileData);
    setProfile(response.data);
    setShouldCompleteProfile(false);
  }, []);

  const clearFavoritesFromSessionStorage = useCallback((userId) => {
    try {
      if (userId) {
        const favoritesKey = `mindthegraph_favorites_all_${userId}`;
        sessionStorage.removeItem(favoritesKey);
      } else {
        const keysToRemove = [];
        for (let i = 0; i < sessionStorage.length; i++) {
          const key = sessionStorage.key(i);
          if (key && key.startsWith('mindthegraph_favorites_all_')) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach((key) => sessionStorage.removeItem(key));
      }
    } catch (e) {
      console.error('Error clearing favorites from session storage:', e);
    }
  }, []);

  /**
   * Remove the user data from the context, and deletes the auth cookie if it exists
   *
   * @returns {void}
   */
  const removeUser = useCallback(() => {
    clearFavoritesFromSessionStorage(user?.id);
    deleteAuthCookie();
    setUser(null);
    setProfile(null);
    setUserJournalSettings(null);
    setIsAuthenticated(false);
  }, [clearFavoritesFromSessionStorage, user?.id]);

  const redirectToLogin = useCallback(async ({ redirectPage, isFullUrl } = {}) => {
    const redirectTo = redirectPage || '/my-creations';
    const redirectUrl = isFullUrl ? redirectPage : `${window.location.origin}${redirectTo}`;
    const geoData = localStorage.getItem('geoData');
    const country = geoData ? JSON.parse(geoData).country_code : 'US';
    const domain =
      country === 'CN' ? EditageConfig.sso.chineseDomain : EditageConfig.sso.mainDomain;
    window.location.href = `${domain}/login?continue=${encodeURIComponent(redirectUrl)}`;
  }, []);

  /**
   * Validate the session, and updates the context if the user is authenticated
   * If the user is not authenticated, it will redirect to the login page
   * If the user is authenticated, it will update the user data in the context
   *
   * @param {Object} options
   * @param {boolean} options.triggerLogin - If the user should be redirected to the login page if not authenticated
   * @param {boolean} options.backgroundValidation - If the validation should be done in the background, not triggering the loading state
   * @param {string} options.redirectTo - The page to redirect to after validation, optional
   * @returns {void}
   */
  const validateSession = useCallback(
    async ({ triggerLogin = false, backgroundValidation = false, redirectTo } = {}) => {
      if (!backgroundValidation) {
        setIsLoading(true);
      }

      try {
        const authStatus = validateAuth();
        const shouldValidateToken =
          !lastTokenValidationTimestamp ||
          Date.now() - lastTokenValidationTimestamp >= TOKEN_VALIDATION_INTERVAL;

        if (
          authStatus === AuthStatusEnum.FULLY_AUTHENTICATED &&
          (!isAuthenticated || shouldValidateToken)
        ) {
          const response = await OneLoginService.validate();

          if (!response.data?.active) {
            removeUser();

            if (triggerLogin) {
              redirectToLogin({ redirectPage: redirectTo || router.pathname });
            }

            return false;
          }

          // Sync states with actual authentication
          if (!isAuthenticated) {
            const { user: userData } = response.data;
            await updateUser(userData);
          }

          setLastTokenValidationTimestamp(Date.now());
          return true;
        }

        if (
          authStatus === AuthStatusEnum.FULLY_AUTHENTICATED_SAML &&
          (!isAuthenticated || shouldValidateToken)
        ) {
          const response = await AuthService.validateSamlUser();

          if (!isAuthenticated && response.data) {
            await updateUser(response.data.user, AuthTypeEnum.SAML);
          }

          setLastTokenValidationTimestamp(Date.now());
          return true;
        }

        // Should not execute on the logout page, as the `__id` will be deleted
        if (
          authStatus === AuthStatusEnum.SSO_AUTHENTICATED &&
          router.pathname !== LOGOUT_PAGE_PATH
        ) {
          const response = await OneLoginService.authenticate();
          const { user: userData } = response.data;
          userData.isNewUser = response.data.isNewUser;
          await updateUser(userData);
          return true;
        }

        if (
          authStatus === AuthStatusEnum.SSO_UNAUTHENTICATED ||
          authStatus === AuthStatusEnum.FULLY_UNAUTHENTICATED
        ) {
          removeUser();

          if (triggerLogin) {
            redirectToLogin({ redirectPage: redirectTo || router.pathname });
          }

          return false;
        }

        return (
          authStatus === AuthStatusEnum.FULLY_AUTHENTICATED ||
          authStatus === AuthStatusEnum.FULLY_AUTHENTICATED_SAML
        );
      } catch (error) {
        console.error('Error validating session:', error);
        if (triggerLogin) {
          redirectToLogin({ redirectPage: redirectTo || router.pathname });
        }
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [
      isAuthenticated,
      lastTokenValidationTimestamp,
      redirectToLogin,
      removeUser,
      router.pathname,
      updateUser,
    ],
  );

  /**
   * Redirect to the complete profile page
   * @param {boolean} continueToWorkspace - If the user should be redirected to the workspace after completing the profile
   * @returns {void}
   */
  const redirectToCompleteProfile = useCallback(
    (overrideParams = '') => {
      let completeProfileUrl = `/auth/complete-profile?`;
      if (overrideParams.length > 0) {
        router.push(`${completeProfileUrl}${encodeURIComponent(overrideParams)}`);
        return;
      }
      const redirectUrl = `${window.location.origin}${router.asPath}`;
      router.push(`${completeProfileUrl}redirect=${encodeURIComponent(redirectUrl)}`);
    },
    [router],
  );

  /**
   * Redirect to the login page
   *
   * @param {Object} options
   * @param {string} options.redirectPage
   * @param {boolean} options.isFullUrl - If the redirect page is a full URL or only the path
   * @returns {void}
   */
  /**x`
   * Redirect to the logout page
   *
   * @param {Object} options
   * @param {string} options.redirectPage
   * @param {boolean} options.isFullUrl - If the redirect page is a full URL or only the path
   * @param {string} options.issLogoutUrl - The URL to logout
   * @returns {void}
   */
  const redirectToLogout = useCallback(async ({ redirectPage, isFullUrl, issLogoutUrl } = {}) => {
    const redirectTo = redirectPage || '/my-creations';
    const redirectUrl = isFullUrl ? redirectPage : `${window.location.origin}${redirectTo}`;

    // Ensure no duplicate "https://" in the logout URL
    let logoutUrl = `https://${issLogoutUrl}/logout?continue=${encodeURIComponent(redirectUrl)}`;

    if (logoutUrl.includes('https://https://')) {
      logoutUrl = logoutUrl.replace('https://https://', 'https://');
    }

    window.location.href = logoutUrl;
  }, []);

  /**
   * Redirect to the register page
   *
   * @param {string} redirectPage - The page to redirect to after registration
   * @returns {void}
   */
  const register = useCallback(
    async (redirectPage) => {
      const redirectTo = redirectPage || router.pathname;
      const redirectUrl = `${window.location.origin}${redirectTo}`;
      window.location.href = `${EditageConfig.sso.mainDomain}/register?continue=${encodeURIComponent(
        redirectUrl,
      )}`;
    },
    [router.pathname],
  );

  /**
   * Login the user, and redirects to the login page if the user is not authenticated
   *
   * @param {Object} options
   * @param {string} options.redirectPage - The page to redirect to after login
   * @param {boolean} options.isFullUrl - If the redirect page is a full URL or only the path
   * @returns {void}
   */
  const login = useCallback(
    async ({ redirectPage, isFullUrl }) => {
      try {
        if (!isAuthenticated) {
          redirectToLogin({ redirectPage, isFullUrl });
          return;
        }

        await validateSession();
      } catch (error) {
        redirectToLogin({ redirectPage, isFullUrl });
      }

      if (redirectPage) {
        router.push(redirectPage);
      } else {
        router.push(router.pathname);
      }
    },
    [isAuthenticated, redirectToLogin, router, validateSession],
  );

  /**
   * Logout the user, and redirects to the logout page
   *
   * @returns {void}
   */
  const logout = useCallback(async () => {
    if (authType === AuthTypeEnum.SAML) {
      removeUser();
      router.push('/');
      return;
    }
    const geoData = localStorage.getItem('geoData');
    const country = geoData ? JSON.parse(geoData).country_code : 'US';
    const domain =
      country === 'CN' ? EditageConfig.sso.chineseDomain : EditageConfig.sso.mainDomain;
    const userEditageToken = Cookies.get(EditageConfig.cookies.idCookieKey);
    const parsedUserEditageToken = userEditageToken ? parseJwt(userEditageToken) : {};
    const issLogoutUrl = parsedUserEditageToken.iss ?? domain;

    removeUser();
    redirectToLogout({ issLogoutUrl });
  }, [authType, redirectToLogout, removeUser]);

  useEffect(() => {
    validateSession();
  }, [validateSession]);

  useEffect(() => {
    const onRouteChange = () => {
      validateSession();
    };

    router.events.on('routeChangeComplete', onRouteChange);
    return () => router.events.off('routeChangeComplete', onRouteChange);
  }, [validateSession]);

  const contextValue = useMemo(
    () => ({
      isAuthenticated,
      isLoading,
      user,
      profile,
      userJournalSettings,
      shouldCompleteProfile,
      login,
      logout,
      register,
      redirectToCompleteProfile,
      completeProfile,
      updateUser,
      authType,
      validateSession,
    }),
    [
      isAuthenticated,
      isLoading,
      user,
      profile,
      userJournalSettings,
      shouldCompleteProfile,
      login,
      logout,
      register,
      redirectToCompleteProfile,
      completeProfile,
      updateUser,
      authType,
      validateSession,
    ],
  );

  const actionsValue = useMemo(
    () => ({
      login,
      logout,
      register,
      redirectToCompleteProfile,
      completeProfile,
      updateUser,
      validateSession,
    }),
    [
      login,
      logout,
      register,
      redirectToCompleteProfile,
      completeProfile,
      updateUser,
      validateSession,
    ],
  );

  const workspaceUserValue = useMemo(() => {
    if (!user) return null;

    return {
      id: user.id,
      email: user.email,
      country_code: user.country_code,
      tp_user: user.tp_user,
      team: user.team,
    };
  }, [user?.id, user?.email, user?.country_code, user?.tp_user, user?.team]);

  return (
    <AuthUserOnlyContext.Provider value={user}>
      <AuthUserWorkspaceContext.Provider value={workspaceUserValue}>
        <AuthUserActionsContext.Provider value={actionsValue}>
          <AuthUserContext.Provider value={contextValue}>{children}</AuthUserContext.Provider>
        </AuthUserActionsContext.Provider>
      </AuthUserWorkspaceContext.Provider>
    </AuthUserOnlyContext.Provider>
  );
}

/**
 * @typedef {Object} AuthUserContextType
 * @property {boolean} isAuthenticated - Boolean indicating if the user is authenticated
 * @property {boolean} isLoading - Boolean indicating if the context is still loading
 * @property {Object} user - User data
 * @property {Object} profile - Profile data
 * @property {Object} userJournalSettings - User journal settings
 * @property {boolean} shouldCompleteProfile - Boolean indicating if the user should complete the profile
 * @property {Function} login - Function to login the user
 * @property {Function} logout - Function to logout the user
 * @property {Function} register - Function to register the user
 * @property {Function} redirectToCompleteProfile - Function to redirect to the complete profile page
 * @property {Function} completeProfile - Function to complete the user profile
 * @property {Function} updateUser - Function to update the user data in the context
 * @property {Function} validateSession - Function to validate the session
 * @property {string} authType - The type of authentication (SAML or SSO)
 */

/**
 * Hook to consume data from the AuthUserContext, including authentication status, user data, profile data, and methods to login, logout, register, and redirect to complete profile.
 *
 * @returns {AuthUserContextType} AuthUserContext data and methods
 * @throws {Error} If the hook is not used within an AuthUserProvider
 */
export const useAuthUser = () => {
  const context = useContext(AuthUserContext);

  if (!context) {
    throw new Error('useAuthUser must be used within an AuthUserProvider');
  }

  return context;
};

export const useAuthUserOnly = () => {
  return useContext(AuthUserOnlyContext);
};

export const useAuthUserActions = () => {
  const actions = useContext(AuthUserActionsContext);
  if (!actions) {
    throw new Error('useAuthUserActions must be used within an AuthUserProvider');
  }
  return actions;
};

export const useAuthWorkspaceUser = () => {
  return useContext(AuthUserWorkspaceContext);
};
